import noTimeSlot from "@/assets/images/no-shift.png";
import { UserWorkScheduleAppointmentDto } from "@/models/club";
import { useCreateShiftSchedule } from "@/services/shiftSchedule/utils";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Drawer,
  Group,
  Image,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCirclePlus, IconTrash } from "@tabler/icons-react";
import moment, { Moment } from "moment-jalaali";
import { useEffect } from "react";
import {
  addHoursToTime,
  AddTimeSlotType,
  generateTimeOptions,
  schema,
  WeekDays,
  WeekDaysFa,
} from "./type";

// Generate time options from 00:00 to 23:55 in 5-minute intervals
const TIME_OPTIONS = generateTimeOptions();

type Props = {
  opened: boolean;
  close: () => void;
  selectedWeekDay: number;
  selectedTeam: UserWorkScheduleAppointmentDto;
  selectedEditDate: Moment;
  // addTimeSlotToDay: (timeSlots: AddWorkScheduleCommandItem) => void;
  existingTimeSlots?: Array<{
    startTime?: { hour?: number; minute?: number };
    endTime?: { hour?: number; minute?: number };
  }> | null;
  refetchShiftSchedule: () => void;
};

function AddTimeSlotForOneDay({
  opened,
  close,
  selectedWeekDay,
  selectedTeam,
  // addTimeSlotToDay,
  existingTimeSlots,
  selectedEditDate,
  refetchShiftSchedule,
}: Props) {
  const { mutate: createShiftSchedule, isLoading } = useCreateShiftSchedule();

  const convertExistingTimeSlots = () => {
    if (!existingTimeSlots || existingTimeSlots.length === 0) {
      return [];
    }

    const timeSlots = existingTimeSlots.map((slot) => ({
      startTime: moment(slot?.startTime, "HH:mm:ss").format("HH:mm"),
      endTime: moment(slot?.endTime, "HH:mm:ss").format("HH:mm"),
    }));

    return timeSlots;
  };

  const form = useForm<AddTimeSlotType>({
    initialValues: {
      timeSlots: [],
    },
    validate: yupResolver(schema),
  });

  useEffect(() => {
    form.setFieldValue("timeSlots", convertExistingTimeSlots());
  }, [existingTimeSlots]);

  const removeItem = (index: number) => {
    return () => {
      const updatedTimeSlots = form.values.timeSlots.filter(
        (_, i) => i !== index
      );
      form.setFieldValue("timeSlots", updatedTimeSlots);
    };
  };

  const addNewTimeSlot = () => {
    const lastTimeSlot =
      form.values.timeSlots[form.values.timeSlots.length - 1];

    let newTimeSlot;
    if (lastTimeSlot) {
      // New slot starts at the last slot's end time, and ends 1 hour later
      newTimeSlot = {
        startTime: addHoursToTime(lastTimeSlot.endTime, 1),
        endTime: addHoursToTime(lastTimeSlot.endTime, 2),
      };
    } else {
      // Default first time slot
      newTimeSlot = { startTime: "09:00", endTime: "17:00" };
    }

    form.setFieldValue("timeSlots", [...form.values.timeSlots, newTimeSlot]);
  };

  const submit = (values: AddTimeSlotType) => {
    const fromMoment = moment(selectedEditDate).format("YYYY-MM-DD");
    const toMoment = moment(selectedEditDate).format("YYYY-MM-DD");

    // Get the weekday number for the selected date (1-7, where 1 is Saturday)
    const selectedWeekDayNumber =
      WeekDaysFa[
      moment(selectedEditDate)
        .locale("fa")
        .format("dd") as keyof typeof WeekDaysFa
      ];

    // Get existing weekDays from the server data
    const existingWeekDays =
      selectedTeam?.workScheduleCalendar?.map((day) => {
        const dayMoment = moment(day?.availableDate);
        const weekDayNumber =
          WeekDaysFa[
          dayMoment.locale("fa").format("dd") as keyof typeof WeekDaysFa
          ];

        return {
          weekDay: weekDayNumber,
          timeSlots:
            day?.timeSlots?.map((slot) => ({
              startTime: slot.startTime,
              endTime: slot.endTime,
            })) || [],
        };
      }) || [];

    // Create the updated weekDays array
    const updatedWeekDays = existingWeekDays.map((existingDay) => {
      if (existingDay.weekDay === selectedWeekDayNumber) {
        // Update the selected day with new time slots
        return {
          weekDay: selectedWeekDayNumber,
          timeSlots: values.timeSlots.map((slot) => ({
            startTime: moment(slot.startTime, "HH:mm").format("HH:mm:ss"),
            endTime: moment(slot.endTime, "HH:mm").format("HH:mm:ss"),
          })),
        };
      }
      return existingDay; // Keep other days unchanged
    });

    // If the selected day doesn't exist in existingWeekDays, add it
    const dayExists = existingWeekDays.some(
      (day) => day.weekDay === selectedWeekDayNumber
    );
    if (!dayExists) {
      updatedWeekDays.push({
        weekDay: selectedWeekDayNumber,
        timeSlots: values.timeSlots.map((slot) => ({
          startTime: moment(slot.startTime, "HH:mm").format("HH:mm:ss"),
          endTime: moment(slot.endTime, "HH:mm").format("HH:mm:ss"),
        })),
      });
    }

    createShiftSchedule(
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        fromDate: fromMoment,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        toDate: toMoment,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        weekDays: updatedWeekDays,
        merchantUserId: selectedTeam?.user?.id,
      },
      {
        onSuccess() {
          notifications.show({
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          refetchShiftSchedule();
          close();
        },
      }
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title=""
      radius="md"
      position="bottom"
      size="lg"
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "71.4svh",
        }}
      >
        <Box style={{ flex: 1 }} px="md" mb="lg">
          <Title order={5} mb="xl" fw="500">
            شیفت برای " {selectedTeam?.user?.firstName} " روز "{" "}
            {moment(selectedEditDate).format("dddd, jDD jMMMM")} "
          </Title>

          <form
            id="add-time-slot"
            name="add-time-slot"
            onSubmit={form.onSubmit(submit)}
          >
            {form.values.timeSlots.length === 0 ? (
              <>
                <Stack
                  justify="center"
                  align="center"
                  flex={1}
                  h="100%"
                  gap="xl"
                  mt="100"
                >
                  <Image src={noTimeSlot} alt="no time slot" w={60} h={60} />
                  <Text>
                    شیفتی برای{" "}
                    {WeekDays[selectedWeekDay as keyof typeof WeekDays]} تعریف
                    نشده است
                  </Text>
                  <Button
                    variant="default"
                    radius="xl"
                    leftSection={<IconCirclePlus stroke={1} size={20} />}
                    onClick={addNewTimeSlot}
                  >
                    افزودن
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Stack>
                  {form.values.timeSlots.map((item, index) => (
                    <Group key={index} align="flex-start" wrap="nowrap">
                      <Select
                        label={index === 0 ? "زمان شروع" : ""}
                        value={item.startTime}
                        size="lg"
                        data={TIME_OPTIONS}
                        onChange={(value) => {
                          form.setFieldValue(
                            `timeSlots.${index}.startTime`,
                            value || ""
                          );
                        }}
                        searchable
                        style={{ flex: 1 }}
                        error={form.errors[`timeSlots.${index}.startTime`]}
                        styles={{
                          label: {
                            fontSize: "0.8rem",
                          },
                          error: {
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                      <Select
                        label={index === 0 ? "زمان پایان" : ""}
                        value={item.endTime}
                        data={TIME_OPTIONS}
                        size="lg"
                        onChange={(value) => {
                          form.setFieldValue(
                            `timeSlots.${index}.endTime`,
                            value || ""
                          );
                        }}
                        searchable
                        style={{ flex: 1 }}
                        error={form.errors[`timeSlots.${index}.endTime`]}
                        styles={{
                          label: {
                            fontSize: "0.8rem",
                          },
                          error: {
                            fontSize: "0.8rem",
                          },
                        }}
                      />

                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="lg"
                        mt={index === 0 ? "xl" : "xs"}
                        onClick={removeItem(index)}
                      >
                        <IconTrash stroke={1} />
                      </ActionIcon>
                    </Group>
                  ))}
                  <Button
                    variant="default"
                    radius="xl"
                    w="fit-content"
                    leftSection={<IconCirclePlus stroke={1} size={20} />}
                    onClick={addNewTimeSlot}
                  >
                    افزودن تایم
                  </Button>
                </Stack>
              </>
            )}
          </form>
        </Box>
      </Box>

      <Card
        styles={{
          root: {
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          },
        }}
        withBorder
        radius={0}
      >
        <Stack>
          {form?.errors?.timeSlots && (
            <Text c="red" size="xs">
              {form?.errors?.timeSlots as string}
            </Text>
          )}

          <Button
            type="submit"
            size="lg"
            form="add-time-slot"
            fullWidth

            loading={isLoading}
          >
            افزودن
          </Button>
        </Stack>
      </Card>
    </Drawer>
  );
}

export default AddTimeSlotForOneDay;
